'use client';

import { useCallback, useMemo, startTransition } from 'react';
import dynamic from 'next/dynamic';
import { ShoppingCart } from '@phosphor-icons/react';
import {
  Button,
  Badge,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetBody,
  SheetFooter,
  QuantitySelector,
} from '@/components/ui';
// Above-the-fold — грузим сразу: hero, каталог, address sheet (откроется по клику),
// preorder banner (показывается выборочно).
import { Catalog, HeroBanner, SmartAppBanner } from '@/components/features';
import { PreorderBanner } from '@/components/features/preorder-banner';
// Неоплаченный заказ — это деньги, которые человек уже хотел заплатить.
// Раньше он не видел его нигде: ни на главной, ни в профиле.
import { UnpaidOrderBanner } from '@/components/features/orders';

// Below-the-fold — lazy load. Эти блоки появляются ПОСЛЕ каталога, до них
// клиент должен доскроллить. Грузить их сразу = тащить JS, который не нужен
// в первые 3 секунды просмотра. ssr:false убирает их из initial HTML, не
// влияя на SEO главной (она и так Client Component).
const MobileAppBanner = dynamic(
  () => import('@/components/features').then((m) => ({ default: m.MobileAppBanner })),
  { ssr: false, loading: () => <div className="h-32" /> }
);
const BlogSection = dynamic(
  () => import('@/components/features').then((m) => ({ default: m.BlogSection })),
  { ssr: false, loading: () => <div className="h-64" /> }
);
const SeoBlock = dynamic(
  () => import('@/components/features').then((m) => ({ default: m.SeoBlock })),
  { ssr: false, loading: () => null }
);
import { useProducts } from '@/hooks/use-products';
import { Header, Footer } from '@/components/layout';
import { useCartStore } from '@/stores/cart-store';
import { useProductPopupStore } from '@/stores/product-popup-store';
import { formatPrice } from '@/lib/utils';
import type { ProductWithCategories } from '@/lib/api/products';
import { toast } from 'sonner';
import { JsonLd } from '@/components/seo/JsonLd';
import { generateRestaurantSchema, generateWebSiteSchema } from '@/lib/seo/json-ld';
import { siteConfig } from '@/config/site';

export default function HomePage() {
  // Pre-fetch products (data is used by Catalog component internally)
  useProducts();
  
  // Get unique categories for navigation (available via productsByCategory)
  // const categories: CategoryAnchor[] = Array.from(productsByCategory.entries()).map(
  //   ([id, { category }]) => ({ id, name: category.name })
  // );
  
  // Cart store - используем селекторы для оптимизации
  const hasHydrated = useCartStore((state) => state._hasHydrated);
  const cartItems = useCartStore((state) => state.items);
  const totalPrice = useCartStore((state) => state.totalPrice);
  const totalItems = useCartStore((state) => state.totalItems);
  const isCartOpen = useCartStore((state) => state.isOpen);
  const setCartOpen = useCartStore((state) => state.setCartOpen);
  const updateQuantity = useCartStore((state) => state.updateQuantity);
  const removeItem = useCartStore((state) => state.removeItem);

  // Hydration-safe values
  const safeCartItems = hasHydrated ? cartItems : [];
  const safeTotalPrice = hasHydrated ? totalPrice : 0;
  const safeTotalItems = hasHydrated ? totalItems : 0;

  // Product Popup store
  const openPopup = useProductPopupStore((state) => state.openPopup);

  // Мемоизированные вычисления
  const formattedTotalPrice = useMemo(() => formatPrice(safeTotalPrice), [safeTotalPrice]);
  const cartDescription = useMemo(() => {
    if (safeTotalItems > 0) {
      return `${safeTotalItems} товара на сумму ${formattedTotalPrice}`;
    }
    return 'Корзина пуста';
  }, [safeTotalItems, formattedTotalPrice]);

  // Handle product click - open popup с startTransition
  const handleProductClick = useCallback((product: ProductWithCategories) => {
    startTransition(() => {
      openPopup(product);
    });
  }, [openPopup]);

  // Мемоизированные обработчики для quantity
  const handleQuantityChange = useCallback((itemId: number, quantity: number) => {
    if (quantity === 0) {
      removeItem(itemId);
    } else {
      updateQuantity(itemId, quantity);
    }
  }, [removeItem, updateQuantity]);

  return (
    <main className="min-h-screen relative bg-[var(--bg-primary)]">
      {/* JSON-LD structured data for search engines */}
      <JsonLd data={generateRestaurantSchema(siteConfig)} />
      <JsonLd data={generateWebSiteSchema(siteConfig)} />

      {/* Gradient mesh - только на десктопе */}
      <div className="hidden md:block fixed inset-0 -z-10 pointer-events-none">
        <div className="absolute inset-0 bg-[var(--bg-primary)]" />
        <div className="absolute inset-0 gradient-mesh opacity-60" />
        <div 
          className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-pill bg-[radial-gradient(circle,color-mix(in_srgb,var(--primary)_8%,transparent)_0%,transparent_70%)]"
        />
        <div 
          className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-pill bg-[radial-gradient(circle,color-mix(in_srgb,var(--primary)_5%,transparent)_0%,transparent_70%)]"
        />
      </div>
      
      {/* Smart App Banner — узкая полоса наверху на mobile/tablet.
          ВРЕМЕННО СКРЫТ по запросу. Чтобы вернуть — раскомментировать строку ниже. */}
      {/* <SmartAppBanner /> */}

      {/* Header */}
      <Header />

      {/* Hero Banner — на мобилке fixed, контент наезжает сверху (параллакс) */}
      <div className="relative md:block">
        <div className="fixed inset-x-0 top-0 z-0 md:hidden">
          <HeroBanner />
        </div>
        <div className="hidden md:block">
          <HeroBanner />
        </div>
      </div>

      {/* Spacer — на мобилке отодвигает контент вниз на высоту баннера */}
      <div className="h-[488px] sm:h-[508px] md:hidden" />

      {/* Content with white background card — наезжает на баннер */}
      <div className="relative z-20">
        <div className="max-w-7xl mx-auto px-0 sm:px-6 -mt-8">
          {/* Скруглённая "крышка" — только визуальный эффект наезда на баннер */}
          <div className="h-8 rounded-t-screen bg-[var(--bg-elevated)] md:hidden" />
          <div className="hidden md:block h-8 rounded-t-screen bg-[var(--bg-elevated)]" />
          {/* Тень купола направлена ВВЕРХ (0 -4px): она подсвечивает наезд каталога
              на баннер. Ни одна системная роль (raised/float/deep) вверх не светит,
              поэтому значение оставлено как есть — прозрачность 0.08 уже совпадает
              с уплощённым float, углублять или переворачивать нечего. */}
          <div className="bg-[var(--bg-elevated)] md:rounded-b-screen relative md:shadow-[0_-4px_30px_rgba(0,0,0,0.08)] dark:shadow-none pl-4 pr-0 sm:px-6 pb-8 md:pt-6 md:pb-12 catalog-container">
            {/* Preorder Banner — only visible outside working hours */}
            <PreorderBanner />

            {/* Заказ с непрошедшей оплатой — с кнопкой доплатить */}
            <UnpaidOrderBanner />

            {/* Catalog Section */}
            <section id="catalog" className="scroll-mt-8">
              <Catalog
                onProductClick={handleProductClick}
              />
            </section>
          </div>
        </div>
        {/* Всё ниже каталога — сплошной фон, перекрывает fixed-баннер */}
        <div className="bg-[var(--bg-primary)] md:bg-transparent">

      {/* Mobile App Promo Banner */}
      <div className="max-w-7xl mx-auto px-0 sm:px-6 py-8 md:py-12 bg-[var(--bg-primary)]">
        <MobileAppBanner />
      </div>

      {/* Blog Section */}
      <BlogSection />

      {/* SEO Block */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8 md:pb-12 bg-[var(--bg-primary)]">
        <SeoBlock />
      </div>

      {/* Cart Sheet */}
      <Sheet open={isCartOpen} onOpenChange={setCartOpen}>
        <SheetContent side="right">
          <SheetHeader>
            <SheetTitle>Корзина</SheetTitle>
            <SheetDescription>
              {cartDescription}
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            {safeCartItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-12">
                <ShoppingCart className="h-16 w-16 text-[var(--text-tertiary)] mb-4" weight="thin" />
                <p className="text-[var(--text-secondary)]">
                  Добавьте товары в корзину
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {safeCartItems.map((item) => (
                  <div 
                    key={item.id} 
                    className="flex gap-3 p-3 rounded-card bg-[var(--surface-secondary)]"
                  >
                    <div
                      className="w-16 h-16 rounded-card bg-[var(--surface-tertiary)] bg-cover bg-center flex-shrink-0"
                      style={{ backgroundImage: `url(${item.product.image_url})` }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{item.product.name}</p>
                      {item.isBonus && (
                        <Badge variant="success" size="xs" className="mt-1">Бонус</Badge>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        <span className="font-semibold">
                          {formatPrice(item.product.price * item.quantity)}
                        </span>
                        {!item.isBonus && (
                          <QuantitySelector 
                            value={item.quantity} 
                            onChange={(q) => handleQuantityChange(item.id, q)} 
                            size="sm" 
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SheetBody>
          {safeCartItems.length > 0 && (
            <SheetFooter>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[var(--text-secondary)]">Итого:</span>
                <span className="text-xl font-bold">{formattedTotalPrice}</span>
              </div>
              <Button 
                className="w-full" 
                onClick={() => {
                  toast.success('Переход к оформлению');
                  // TODO: Navigate to checkout
                }}
              >
                Оформить заказ
              </Button>
            </SheetFooter>
          )}
        </SheetContent>
      </Sheet>

      {/* Footer */}
      <Footer className="bg-[var(--bg-primary)]" />
      </div>
      </div>
    </main>
  );
}
